import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import type { LoginDTO } from '@shared/schemas/auth';
import { loginSchema } from '@shared/schemas/auth';
import { authClient } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

type LoginFormProps = React.ComponentProps<'form'> & {
  redirectTo?: string;
};

export function LoginForm({
  className,
  redirectTo = '/',
  ...props
}: LoginFormProps) {
  const navigate = useNavigate();

  const form = useForm<LoginDTO>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(loginSchema),
  });

  const redirectTarget = useMemo(() => redirectTo || '/', [redirectTo]);

  async function handleLogin(values: LoginDTO) {
    form.clearErrors('root');

    const result = await authClient.signIn.email({
      email: values.email,
      password: values.password,
      rememberMe: true,
    });

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);

      if ('code' in result.error && result.error.code === 'EMAIL_NOT_VERIFIED') {
        navigate(`/auth/verify-email?email=${encodeURIComponent(values.email)}`);
      }

      return;
    }

    await authClient.getSession();
    toast.success('Connexion réussie.');
    navigate(redirectTarget, { replace: true });
  }

  return (
    <form
      className={cn('flex flex-col gap-6', className)}
      onSubmit={form.handleSubmit(handleLogin)}
      {...props}
    >
      <FieldGroup>
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight">Connectez-vous à votre espace</h1>
            <p className="text-sm text-balance text-muted-foreground">
              Saisissez vos identifiants professionnels pour accéder à la console.
            </p>
          </div>
        </div>

        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            data-testid="login-email"
            type="email"
            autoComplete="email"
            placeholder="nom@entreprise.fr"
            className="bg-background"
            aria-invalid={form.formState.errors.email ? 'true' : 'false'}
            {...form.register('email')}
          />
          <FieldError errors={[form.formState.errors.email]} />
        </Field>

        <Field>
          <div className="flex items-center">
            <FieldLabel htmlFor="password">Mot de passe</FieldLabel>
            <Link className="ml-auto text-sm text-muted-foreground hover:text-foreground" to="/auth/forgot-password">
              Mot de passe oublié ?
            </Link>
          </div>
          <Input
            id="password"
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            placeholder="Votre mot de passe"
            className="bg-background"
            aria-invalid={form.formState.errors.password ? 'true' : 'false'}
            {...form.register('password')}
          />
          <FieldError errors={[form.formState.errors.password]} />
        </Field>

        <FieldError errors={[form.formState.errors.root]} />

        <Field>
          <Button data-testid="login-submit" type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
            Se connecter
          </Button>
        </Field>

        <FieldDescription className="text-center">
          Les accès sont créés par un administrateur. Si ton compte n&apos;est pas encore actif, contacte ton responsable.
        </FieldDescription>
      </FieldGroup>
    </form>
  );
}
